/**
 * Period tabs, rendered from the registry the API returns.
 *
 * Adding a period server-side (columns.PERIODS) adds a tab here with no change
 * to this file. Implemented as a real tablist so arrow keys work and screen
 * readers announce the selected period.
 */
export default function PeriodTabs({ periods, activeKey, onChange, disabled }) {
  if (!periods?.length) return null;

  const handleKeyDown = (event) => {
    const offset = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!offset) return;
    event.preventDefault();
    const index = periods.findIndex((period) => period.key === activeKey);
    const next = periods[(index + offset + periods.length) % periods.length];
    onChange(next.key);
  };

  return (
    <div
      role="tablist"
      aria-label="Reporting period"
      onKeyDown={handleKeyDown}
      className="inline-flex items-center gap-1 rounded-2xl border border-gray-200 bg-white p-1 shadow-sm"
    >
      {periods.map((period) => {
        const isActive = period.key === activeKey;
        return (
          <button
            key={period.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            // Only the active tab is in the tab order; arrow keys move between them.
            tabIndex={isActive ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(period.key)}
            title={period.subtitle}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#144677]/40 disabled:cursor-not-allowed disabled:opacity-60 ${
              isActive
                ? "bg-[#144677] text-white shadow-sm"
                : "text-gray-500 hover:bg-gray-50 hover:text-[#144677] cursor-pointer"
            }`}
          >
            <span>{period.short}</span>
            <span
              className={`hidden text-[11px] font-medium sm:inline ${
                isActive ? "text-white/70" : "text-gray-400"
              }`}
            >
              {period.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
